/**
 * RelatedTasksCTA Component
 *
 * Call-to-action button for adding related tasks.
 * Displayed when no tasks are currently selected.
 *
 * Features:
 * - Dashed border card design
 * - Hover effect
 * - Icon + Title + Description layout
 * - Accessible (keyboard support)
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/components
 */

import React from 'react';
import { Stack, Text } from '@mantine/core';
import { IconLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import styles from './RelatedTasksCTA.module.css';

interface RelatedTasksCTAProps {
  onClick: () => void;
}

export const RelatedTasksCTA: React.FC<RelatedTasksCTAProps> = ({
  onClick,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={styles.ctaCard}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={t('wizard.step4.cta.title')}
    >
      <Stack align="center" gap="sm">
        <IconLink size={48} className={styles.ctaIcon} />
        <Text fw={FONT_WEIGHT_SEMIBOLD} size="md" c="dimmed">
          {t('wizard.step4.cta.title')}
        </Text>
        <Text
          size="sm"
          c="dimmed"
          ta="center"
          className={styles.ctaDescription}
        >
          {t('wizard.step4.cta.description')}
        </Text>
      </Stack>
    </div>
  );
};
